import { useFindFileUploadQuery } from "@qw/graphql";
import { Badge, fileExtensionFromMimeType, formatFileSize, getFileIcon, Icon, parseFilename, Spinner } from "@qw/ui";
import { type ChainedCommands, mergeAttributes, Node } from "@tiptap/core";
import { Selection, type Transaction } from "@tiptap/pm/state";
import { NodeViewWrapper, type ReactNodeViewProps, ReactNodeViewRenderer, useCurrentEditor } from "@tiptap/react";
import { useMemo } from "react";

interface FileUploadNodeConfig {
  nodeName: string;
  commandsNamespace: string;
  commandName: string;
  dataType: string;
  className?: string;
}

const escapeHtml = (value: string): string =>
  value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const createFileUploadNodeComponent = (config: FileUploadNodeConfig) => {
  return (props: ReactNodeViewProps<HTMLDivElement>) => {
    const { editor } = useCurrentEditor();
    const { node, getPos } = props;

    // NOTE: For some reason, the node view is not selected when the user clicks on the node.
    // This is a workaround to select the node when the user clicks on it.
    // We use the editor command to set a node selection at the current node's position.
    const selectNode = () => editor?.commands.setNodeSelection(getPos() ?? 0);

    const { data, loading } = useFindFileUploadQuery({
      variables: { input: { id: node.attrs.fileUploadId } },
    });

    const file = data?.findFile;
    const { name } = parseFilename(file?.filename || "");

    const icon = useMemo(() => getFileIcon(file?.mimeType || ""), [file?.mimeType]);

    return (
      <NodeViewWrapper
        onClick={selectNode}
        className={`${config.className || "editor-extension-file"} my-2 max-w-full`}
      >
        <div className="relative flex items-center bg-popover border border-border rounded-md py-2 px-3 gap-2 text-sm in-[.ProseMirror-selectednode]:ring-2 in-[.ProseMirror-selectednode]:ring-primary/50 selection:bg-transparent">
          {loading ? (
            <Spinner className="size-3" />
          ) : (
            <Icon icon={icon} className="size-3 text-sm text-foreground opacity-50 filled" />
          )}

          {loading ? (
            <span>Loading...</span>
          ) : file ? (
            <>
              <span>{name}</span>
              <span className="flex-1" />
              <span className="text-xs text-muted-foreground">{formatFileSize(file?.size)}</span>
              <Badge variant="secondary" size="2xs" type="square" className="uppercase">
                {fileExtensionFromMimeType(file?.mimeType || "")}
              </Badge>
            </>
          ) : (
            <span>File not found</span>
          )}
        </div>
      </NodeViewWrapper>
    );
  };
};

export const createFileUploadExtension = (config: FileUploadNodeConfig) => {
  return Node.create({
    name: config.nodeName,
    group: "block",
    atom: true,

    addAttributes() {
      return {
        fileUploadId: {
          default: "",
        },
      };
    },

    addCommands() {
      return {
        [config.commandName]:
          (fileUploadId?: string) =>
          ({ chain }: { chain: () => ChainedCommands }) => {
            if (!fileUploadId) {
              return false;
            }

            return chain()
              .insertContent({ type: config.nodeName, attrs: { fileUploadId } })
              .command(({ tr, dispatch }: { tr: Transaction; dispatch: ((tr: Transaction) => void) | undefined }) => {
                if (dispatch) {
                  const { selection } = tr;
                  const pos = selection.to;

                  // If there is no node after the insertion, add a paragraph
                  if (!tr.doc.nodeAt(pos)) {
                    tr.insert(pos, tr.doc.type.schema.nodes.paragraph.create());
                  }

                  // Move cursor to the next available selection position
                  const newSelection = Selection.near(tr.doc.resolve(pos), 1);
                  if (newSelection) {
                    tr.setSelection(newSelection);
                  }
                }

                return true;
              })
              .focus()
              .run();
          },
      };
    },

    parseHTML() {
      return [
        {
          priority: 100,
          tag: `div[data-type="${config.dataType}"]`,
          getAttrs(node) {
            if (typeof node === "string") {
              return false;
            }
            const fileUploadId = node.dataset.fileUploadId;
            return { fileUploadId };
          },
        },
      ];
    },

    renderHTML({ node }) {
      return [
        "div",
        mergeAttributes({ "data-type": config.dataType, "data-file-upload-id": node.attrs.fileUploadId }),
        node.attrs.fileUploadId,
      ];
    },

    renderMarkdown({ attrs }) {
      const fileUploadId = String(attrs?.fileUploadId ?? "");

      if (!fileUploadId) {
        return "";
      }

      return `<div data-type="${escapeHtml(config.dataType)}" data-file-upload-id="${escapeHtml(fileUploadId)}">${escapeHtml(fileUploadId)}</div>`;
    },

    addNodeView() {
      return ReactNodeViewRenderer(createFileUploadNodeComponent(config));
    },
  });
};

// Backwards compatibility - export the playbook file extension with the original name
declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    PlaybookFiles: {
      insertPlaybookFile: (fileUploadId?: string) => ReturnType;
    };
  }
}

export const EditorExtensionPlaybookFile = createFileUploadExtension({
  nodeName: "playbookFile",
  commandsNamespace: "PlaybookFiles",
  commandName: "insertPlaybookFile",
  dataType: "playbook-file",
  className: "editor-extension-playbook-file",
});
